/**
 * bakeLayout — client-side probe/capture placement preview for the Bake Lighting
 * settings UI (ENG-561). Mirrors the server bake's grid/seed math so the gizmos
 * + counts shown to the user match what the bake will produce.
 *
 * NOTE: the server trims to a dense region of the splat; here we use the loaded
 * splat's full bounding box, so positions are *approximately* where probes land
 * (density reads correctly; exact extent may differ).
 */
import * as THREE from 'three'

/**
 * World-space bounds of the loaded splat + floor height, or null if none.
 *
 * ASKED OF THE GEOMETRY, and it has to be: three's `GaussianSplat` computes its
 * bounds lazily into `boundingBox` and has no `getBoundingBox()` — the method the
 * previous splat renderer offered. Reading the missing method returned null here,
 * and null is a SUPPORTED answer ("no environment loaded"), so the bake panel's
 * probe preview quietly showed nothing over a room that was fully loaded.
 *
 * The splat's world placement is BAKED INTO ITS ATTRIBUTES (see
 * `ThreeEnvironmentSplat`), so the geometry's own box is already world-space and
 * `matrixWorld` is the identity. It is applied anyway rather than assumed: a
 * caller that ever poses the mesh instead would otherwise get bounds that are
 * plausible and wrong.
 */
export function getSplatBounds() {
  if (typeof window === 'undefined') return null
  const m = window.__SPLAT_MESH__
  if (!m) return null
  try {
    if (m.boundingBox === null || m.boundingBox === undefined) m.computeBoundingBox?.()
    const src = m.boundingBox ?? m.geometry?.boundingBox
    if (!src) return null
    m.updateMatrixWorld(true)
    const box = src.clone().applyMatrix4(m.matrixWorld)
    const floorY = (window.__ENV_FLOOR_Y__ != null) ? window.__ENV_FLOOR_Y__ : box.min.y
    return {
      min: [box.min.x, box.min.y, box.min.z],
      max: [box.max.x, box.max.y, box.max.z],
      floorY,
    }
  } catch {
    return null
  }
}

/** Evenly distribute N layer heights (floor-relative) between 0.9 and 2.4 m. */
export function layerHeights(n) {
  const count = Math.max(1, Math.round(n))
  if (count === 1) return [1.4]
  const lo = 0.9, hi = 2.4
  return Array.from({ length: count }, (_, i) => lo + (hi - lo) * (i / (count - 1)))
}

/** Light-probe grid dimensions (nx, nz, layers) without allocating positions. */
export function lightProbeDims(bounds, spacing, layers) {
  if (!bounds || !(spacing > 0)) return { nx: 0, nz: 0, ny: 0 }
  const [minx, , minz] = bounds.min
  const [maxx, , maxz] = bounds.max
  return {
    nx: Math.max(2, Math.ceil((maxx - minx) / spacing) + 1),
    nz: Math.max(2, Math.ceil((maxz - minz) / spacing) + 1),
    ny: Math.max(1, Math.round(layers)),
  }
}

/** Light-probe count without allocating the position array. */
export function lightProbeCount(bounds, spacing, layers) {
  const { nx, nz, ny } = lightProbeDims(bounds, spacing, layers)
  return nx * nz * ny
}

/** Reflection-capture count without allocating the position array. */
export function reflectionCount(bounds, spacing, inset) {
  if (!bounds || !(spacing > 0)) return 0
  return axisSamples(bounds.min[0], bounds.max[0], inset, spacing).length
    * axisSamples(bounds.min[2], bounds.max[2], inset, spacing).length
}

/** Light-probe grid positions (mirrors lightprobes.bake_grid). */
export function lightProbePositions(bounds, spacing, layers) {
  if (!bounds || !(spacing > 0)) return []
  const [minx, , minz] = bounds.min
  const [maxx, , maxz] = bounds.max
  const nx = Math.max(2, Math.ceil((maxx - minx) / spacing) + 1)
  const nz = Math.max(2, Math.ceil((maxz - minz) / spacing) + 1)
  const ys = layerHeights(layers).map(h => bounds.floorY + h)
  const pts = []
  for (const y of ys) {
    for (let iz = 0; iz < nz; iz++) {
      for (let ix = 0; ix < nx; ix++) pts.push([minx + ix * spacing, y, minz + iz * spacing])
    }
  }
  return pts
}

function axisSamples(lo, hi, inset, spacing) {
  const a = lo + inset, b = hi - inset
  if (b <= a) return [(lo + hi) / 2]
  const n = Math.max(1, Math.round((b - a) / spacing)) + 1
  if (n === 1) return [(a + b) / 2]
  return Array.from({ length: n }, (_, i) => a + (b - a) * (i / (n - 1)))
}

/** Reflection-capture positions on the floor plane (mirrors seeding.seed_positions). */
export function reflectionPositions(bounds, spacing, inset) {
  if (!bounds || !(spacing > 0)) return []
  const xs = axisSamples(bounds.min[0], bounds.max[0], inset, spacing)
  const zs = axisSamples(bounds.min[2], bounds.max[2], inset, spacing)
  const y = bounds.floorY + 1.4
  const pts = []
  for (const z of zs) for (const x of xs) pts.push([x, y, z])
  return pts
}
